import { i18n } from "@/i18n"
import { IArrayFieldConfig } from "@/types"
import type { Content, ContentItem, Title, Track, Chapter, ImageData, StreamingData } from '@/types'

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return import.meta.env[key] || defaultValue
}

export const getTheme = (): string => {
  return getEnvVar('VITE_DEFAULT_THEME', 'heard')
}

export const translate = (key: string, params?: Record<string, any>): string => {
  return i18n.global.t(key, params || {}) as string
}

export function mergeFullNames(items: Array<{ name?: string, surname?: string }>): string {
  return items
    .map(item => {
      const fullName = [item.name, item.surname]
        .filter(Boolean) // Remove undefined/null/empty values
        .join(' ')
        .trim()
      
      return fullName || undefined // Return undefined for empty strings
    })
    .filter(Boolean)
    .join(', ')
}

const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma']
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'm3u8', 'mpd']

function playbackFileExtension(url: string): string {
  if (!url) return ''
  return url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || ''
}

/**
 * Classify a playback URL. Progressive audio wins over a sibling HLS `streaming`
 * object so Heard (video feature off) still plays the mp3/m4a.
 */
export function detectPlaybackContentType(
  url: string,
  source?: {
    content_type?: string
    streaming?: unknown
    chapters?: Array<{ streaming?: unknown }>
  },
): 'audio' | 'video' {
  if (source?.content_type) {
    return source.content_type === 'video' ? 'video' : 'audio'
  }

  const extension = playbackFileExtension(url)
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio'
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video'

  if (source?.streaming) return 'video'
  if (source?.chapters?.some(ch => ch.streaming)) return 'video'

  return 'audio'
}

export const contentToTrack = (content: any): Track => {
  // Check for direct URL first, then try to get URL from first chapter if available
  const url = content.url || (content.chapters && content.chapters.length > 0 ? content.chapters[0].url : '')
  
  return {
    id: content.id,
    type: detectPlaybackContentType(url, content),
    title: content.title,
    artist: content.artist || (content.authors?.length 
      ? `${content.authors[0].name} ${content.authors[0].surname || ''}`.trim() 
      : ''),
    artwork: typeof content.artwork === 'string' ? content.artwork : 
             (typeof content.image === 'string' ? content.image : 
              (content.image?.src || content.artwork?.src || '')),
    url,
    duration: content.duration || 0
  }
}

/** Playback URL for a chapter — prefers direct URL, falls back to HLS/DASH streaming default. */
/**
 * Whether a title payload needs a full API fetch before playback.
 * List/home tiles often include only the first chapter (or chapters missing URLs).
 */
export function titleNeedsFullChapterFetch(title: Title | { chapters?: Chapter[] }): boolean {
  const chapters = title.chapters ?? []
  if (chapters.length === 0) return true
  if (!chapters.every(ch => getChapterPlaybackUrl(ch))) return true
  // Single-chapter stubs are common in list payloads for multi-chapter titles
  if (chapters.length === 1) return true
  return false
}

function pickStreamingString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value
    }
  }
  return undefined
}

const HLS_PLAYLIST_RE = /playlist_(\d+p)\.m3u8/i
const HLS_QUALITY_LABELS = ['360p', '720p', '1080p'] as const

function asQualityMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}

  const map: Record<string, string> = {}
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === 'string' && entry.trim() !== '') {
      map[key] = entry
      continue
    }
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const url = pickStreamingString(
        (entry as Record<string, unknown>).url,
        (entry as Record<string, unknown>).default_url,
        (entry as Record<string, unknown>).defaultUrl,
        (entry as Record<string, unknown>).src,
      )
      if (url) map[key] = url
    }
  }
  return map
}

/** When the API only sends a single playlist_720p.m3u8 URL, recover sibling renditions. */
export function inferHlsQualitiesFromUrl(url?: string): Record<string, string> {
  if (!url || !HLS_PLAYLIST_RE.test(url)) return {}
  const qualities: Record<string, string> = {}
  for (const quality of HLS_QUALITY_LABELS) {
    qualities[quality] = url.replace(HLS_PLAYLIST_RE, `playlist_${quality}.m3u8`)
  }
  return qualities
}

/** JSON:API transform camelCases streaming keys; player code uses snake_case. */
export function normalizeStreamingData(streaming: unknown): StreamingData | null {
  if (!streaming || typeof streaming !== 'object') return null

  const record = streaming as Record<string, unknown>
  let available_qualities = asQualityMap(
    record.available_qualities ?? record.availableQualities,
  )
  const default_url = pickStreamingString(record.default_url, record.defaultUrl)
  if (Object.keys(available_qualities).length === 0) {
    available_qualities = inferHlsQualitiesFromUrl(default_url)
  }

  const default_quality = pickStreamingString(
    record.default_quality,
    record.defaultQuality,
    available_qualities['720p'] ? '720p' : Object.keys(available_qualities)[0],
  )
  const type = pickStreamingString(record.type)
  const resolvedUrl = default_url
    ?? (default_quality ? available_qualities[default_quality] : undefined)
    ?? Object.values(available_qualities)[0]

  if (!resolvedUrl && !default_quality && !type && Object.keys(available_qualities).length === 0) {
    return null
  }

  return {
    default_quality,
    default_url: resolvedUrl,
    available_qualities,
    type,
  }
}

export function getChapterPlaybackUrl(chapter: Chapter | Record<string, unknown>): string {
  const record = chapter as Record<string, unknown>
  const directUrl = record.url
  if (typeof directUrl === 'string' && directUrl.trim() !== '') {
    return directUrl
  }

  const streamingUrl = normalizeStreamingData(record.streaming)?.default_url
  return streamingUrl ?? ''
}

export const chaptersToTracks = (title: any, chapters: any[]): Track[] => {
  return chapters.map(chapter => {
    const playbackUrl = getChapterPlaybackUrl(chapter)
    return {
    id: chapter.id || `${title.id}-${chapter.mediaId || chapter.order}`,
    title: chapter.name,
    artist: title.authors?.length 
      ? `${title.authors[0].name} ${title.authors[0].surname || ''}`.trim() 
      : '',
    artwork: typeof title.image === 'string' ? title.image : (title.image?.src || ''),
    type: detectPlaybackContentType(playbackUrl, chapter),
    url: playbackUrl,
    duration: chapter.duration || 0,
    subtitle: chapter.name, // Use chapter name as subtitle
    description: chapter.description || '',
    isPeekPreview: Boolean(
      (chapter as Chapter & { isPeekPreview?: boolean; is_peek_preview?: boolean }).isPeekPreview ??
        (chapter as Chapter & { is_peek_preview?: boolean }).is_peek_preview
    ),
    }
  })
}

const getNonEmptyString = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value
    }
  }

  return null
}

const JSON_BUILDER_NODE_TYPES = new Set([
  'text',
  'number',
  'boolean',
  'url',
  'date',
  'datetime',
  'resource',
  'image',
  'file',
  'object',
  'list',
  'null',
])

function isJsonBuilderValueEnvelope(value: unknown): value is { type: string; value: unknown } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const obj = value as Record<string, unknown>
  if (!JSON_BUILDER_NODE_TYPES.has(String(obj.type)) || !('value' in obj)) return false
  if ('children' in obj || 'items' in obj || 'id' in obj || 'key' in obj) return false

  return true
}

function unwrapJsonBuilderValue(value: unknown): unknown {
  let current = value
  while (isJsonBuilderValueEnvelope(current)) {
    current = current.value
  }
  return current
}

function parseJsonIfString(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return value
  try {
    return JSON.parse(trimmed)
  } catch {
    return value
  }
}

function normalizeCustomDataRecord(raw: unknown): Record<string, unknown> {
  const parsed = unwrapJsonBuilderValue(parseJsonIfString(raw))

  if (Array.isArray(parsed)) {
    const result: Record<string, unknown> = {}
    for (const item of parsed) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue
      const rec = item as Record<string, unknown>
      const key = rec.key ?? rec.name
      if (typeof key === 'string' && key.trim()) {
        result[key.trim()] = unwrapJsonBuilderValue(rec.value)
      }
    }
    return result
  }

  if (parsed && typeof parsed === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      result[key] = unwrapJsonBuilderValue(value)
    }
    return result
  }

  return {}
}

/** Read `customData` from a title (API attribute or pre-transform snake_case). */
export function getTitleCustomData(
  title: Title | ContentItem | null | undefined
): Record<string, unknown> {
  if (!title || typeof title !== 'object') return {}

  const record = title as Record<string, unknown>
  return normalizeCustomDataRecord(record['customData'] ?? record['custom_data'])
}

/**
 * Get the display title for a title item, preferring `customData.overrideTitle`.
 * @param title - The title object
 * @returns The display title (override if available and not empty, otherwise title)
 */
export const getDisplayTitle = (title: Title | ContentItem | string | null | undefined): string => {
  if (typeof title === 'string') return title
  if (!title || typeof title !== 'object') return ''

  const record = title as Record<string, unknown>
  const customData = getTitleCustomData(title)

  const overrideTitle = getNonEmptyString(
    customData['overrideTitle'],
    record['override_title'],
    record['overrideTitle'],
    record['title_override'],
    record['titleOverride'],
  )

  if (overrideTitle) return overrideTitle

  if ('title' in title && title.title) return title.title

  if ('name' in title && title.name) return title.name as string

  return ''
}

/**
 * Get the display subtitle for a title item, preferring `customData.overrideSubtitle`.
 * @param title - The title object
 * @returns The display subtitle (override if available and not empty, otherwise subtitle)
 */
export const getDisplaySubtitle = (title: Title | ContentItem | string | null | undefined): string => {
  if (typeof title === 'string' || !title || typeof title !== 'object') return ''

  const record = title as Record<string, unknown>
  const customData = getTitleCustomData(title)

  const overrideSubtitle = getNonEmptyString(
    customData['overrideSubtitle'],
    record['override_subtitle'],
    record['overrideSubtitle'],
    record['subtitle_override'],
    record['subtitleOverride'],
  )

  if (overrideSubtitle) return overrideSubtitle

  if ('subtitle' in title && title.subtitle) return title.subtitle

  return ''
}

/**
 * Label for recommendation carousels; falls back to display title.
 * @see docs/implementations/title-custom-data-spec.md
 */
export const getDisplayRecommendationTitle = (
  title: Title | ContentItem | null | undefined
): string => {
  if (!title) return ''

  const record = title as Record<string, unknown>
  const customData = getTitleCustomData(title)

  const override = getNonEmptyString(
    customData['overrideRecommendationTitle'],
    record['override_recommendation_title'],
    record['overrideRecommendationTitle'],
  )

  if (override) return override

  return getDisplayTitle(title)
}

/** Title aggregate from API: `has_peek_preview_chapters` (camelCase after JSON:API transform). */
export function titleHasPeekPreviewChapters(title: Title): boolean {
  const rec = title as Title & {
    hasPeekPreviewChapters?: boolean
    has_peek_preview_chapters?: boolean
  }
  return Boolean(rec.hasPeekPreviewChapters ?? rec.has_peek_preview_chapters)
}

/** Chapter flag: `is_peek_preview` → `isPeekPreview` after transform. */
export function chapterIsPeekPreview(chapter: Chapter): boolean {
  const rec = chapter as Chapter & { isPeekPreview?: boolean; is_peek_preview?: boolean }
  return Boolean(rec.isPeekPreview ?? rec.is_peek_preview)
}

/** Subscription required, not ad-supported only (aligned with useTitleAccess). */
export function isSubscriptionOnlyTitle(title: Title): boolean {
  const sub = title.isSubscription || title.is_subscription
  const ads = title.isAdsupported || title.is_adsupported
  return Boolean(sub && !ads)
}

/** Purchase required for full catalog; not subscription catalog (aligned with useTitleAccess). */
export function isPurchaseOnlyTitle(title: Title): boolean {
  const purch = title.isPurchasable || title.is_purchasable
  const sub = title.isSubscription || title.is_subscription
  const ads = title.isAdsupported || title.is_adsupported
  return Boolean(purch && !sub && !ads)
}

/** Whether the user may play all active chapters (mirrors resolveChaptersForPlayback). */
export function hasFullChapterAccessForPlayback(
  title: Title,
  ctx: PlaybackChapterContext
): boolean {
  if (ctx.purchased) return true
  if (isPurchaseOnlyTitle(title)) return false
  if (isSubscriptionOnlyTitle(title)) return ctx.subscribed
  return true
}

/** Active chapters sorted by `order` (excludes inactive). */
export function sortChaptersActive(chapters: Chapter[]): Chapter[] {
  return [...chapters]
    .filter(ch => (ch as { status?: string }).status !== 'inactive')
    .sort((a, b) => {
      const orderA = Number(a.order ?? a.id ?? 0)
      const orderB = Number(b.order ?? b.id ?? 0)
      return orderA - orderB
    })
}

export function getActivePeekPreviewChapters(chapters: Chapter[]): Chapter[] {
  return sortChaptersActive(chapters).filter(ch => chapterIsPeekPreview(ch))
}

export interface PlaybackChapterContext {
  purchased: boolean
  subscribed: boolean
}

/**
 * Chapters the user may queue: full catalog if purchased or subscribed; on subscription-only
 * titles otherwise only active sneak-peek (`is_peek_preview`) chapters.
 */
export function resolveChaptersForPlayback(
  title: Title,
  ctx: PlaybackChapterContext
): Chapter[] | null {
  const raw = title.chapters
  if (!raw?.length) return null

  if (ctx.purchased) {
    return sortChaptersActive(raw)
  }

  if (isPurchaseOnlyTitle(title)) {
    const peek = getActivePeekPreviewChapters(raw)
    return peek.length > 0 ? peek : null
  }

  if (ctx.subscribed) {
    return sortChaptersActive(raw)
  }

  if (isSubscriptionOnlyTitle(title)) {
    const peek = getActivePeekPreviewChapters(raw)
    return peek.length > 0 ? peek : null
  }

  return sortChaptersActive(raw)
}

/**
 * After the last queued sneak-peek chapter ends, which unlock action to show.
 * When both subscription and purchase apply, subscription is offered first.
 */
export function getPeekContinueListeningAction(
  title: Title,
  ctx: PlaybackChapterContext
): 'subscribe' | 'purchase' | null {
  if (ctx.purchased) return null
  const sub = Boolean(title.isSubscription || title.is_subscription)
  const purch = Boolean(title.isPurchasable || title.is_purchasable)
  const ads = Boolean(title.isAdsupported || title.is_adsupported)

  if (sub && !ads && !ctx.subscribed) {
    return 'subscribe'
  }
  if (purch && !ctx.purchased) {
    return 'purchase'
  }
  return null
}

/**
 * Get the client's IP address using a third-party service
 * This is required for Stripe mandate data when using payment methods like Link
 * @returns Promise resolving to the IP address string, or null if unavailable
 */
export const getClientIpAddress = async (): Promise<string | null> => {
  try {
    // Use ipify.org as a reliable free service
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      // Add timeout to prevent hanging
      signal: AbortSignal.timeout(5000)
    })

    if (!response.ok) {
      throw new Error(`IP detection service returned ${response.status}`)
    }

    const data = await response.json()
    const ipAddress = data.ip

    if (ipAddress && typeof ipAddress === 'string') {
      return ipAddress
    }

    return null
  } catch (error) {
    // Log error but don't throw - we'll handle null IP gracefully
    console.warn('Failed to get client IP address:', error)
    return null
  }
}